/**
 * Camp Essentials > Gear — find camp gear stores near a location.
 */
import React, { useState, useCallback, useMemo, forwardRef } from 'react';
import { FaMapMarkerAlt, FaLocationArrow } from 'react-icons/fa';
import { PlaceSearchInput } from '../../../components/ui/PlaceSearchInput';
import { YButton } from '../../../ui/YButton';
import { YSelect } from '../../../ui/YSelect';
import { toolkitService } from '../../toolkit/services/toolkitService';
import { filterStores } from '../campEssentialsFilters';
import './CampGearFinder.css';
import './camp-essentials-gear.css';

const RADIUS_CHOICES = [10, 20, 25, 50, 75, 100];
const MY_LOCATION_RADIUS_MI = 20;

function formatStoreTags(store) {
  const tags = [];
  if (store.priority_label) tags.push(store.priority_label);
  if (store.brand) tags.push(store.brand);
  const inv = Array.isArray(store.inventory_tags) ? store.inventory_tags : [];
  inv.slice(0, 2).forEach((t) => {
    const label = String(t).split(':').pop()?.replace(/_/g, ' ');
    if (label) tags.push(label);
  });
  return tags.slice(0, 3);
}

function StoreCard({ store, viewMode, savedIds, onSave, storeId, onOpen }) {
  const tags = formatStoreTags(store);
  const distance =
    typeof store.distance_miles === 'number' ? `${store.distance_miles.toFixed(1)} mi` : null;
  const address = store.address || [store.city, store.state].filter(Boolean).join(', ');

  return (
    <button
      type="button"
      className={`camp-gear-store-card camp-gear-store-card--${viewMode}`}
      onClick={() => onOpen(store)}
    >
      <div className="camp-gear-store-card__media">
        <div className="camp-gear-store-card__media-overlay" aria-hidden="true" />
        <div className="camp-gear-store-card__badges">
          {distance ? (
            <span className="camp-gear-store-card__badge camp-gear-store-card__badge--distance">
              {distance}
            </span>
          ) : null}
        </div>
      </div>
      <div className="camp-gear-store-card__body">
        <div className="camp-gear-store-card__head">
          <h3 className="camp-gear-store-card__name">{store.name || 'Store'}</h3>
        </div>
        {address ? <p className="camp-gear-store-card__address">{address}</p> : null}
        {tags.length > 0 ? (
          <div className="camp-gear-store-card__tags">
            {tags.map((tag) => (
              <span key={tag} className="camp-gear-store-card__tag">
                {tag}
              </span>
            ))}
          </div>
        ) : null}
        <div className="camp-gear-store-card__actions">
          <YButton
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onSave(store);
            }}
            disabled={savedIds.has(storeId(store))}
          >
            {savedIds.has(storeId(store)) ? 'Saved' : 'Save'}
          </YButton>
          {store.urls?.directions ? (
            <YButton
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                window.open(store.urls.directions, '_blank', 'noopener,noreferrer');
              }}
            >
              Directions
            </YButton>
          ) : null}
        </div>
      </div>
    </button>
  );
}

export const CampGearFinder = forwardRef(function CampGearFinder(
  {
    onSuggestStore,
    getSavedStores,
    saveStore,
    storeId: storeIdFn,
    searchQuery = '',
    categoryId = 'all',
    viewMode = 'grid',
    onChangeLocation,
  },
  ref
) {
  const [query, setQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [radius, setRadius] = useState(MY_LOCATION_RADIUS_MI);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState([]);
  const [highlighted, setHighlighted] = useState([]);
  const [meta, setMeta] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [locationFormOpen, setLocationFormOpen] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    if (getSavedStores) {
      getSavedStores()
        .then((list) => {
          if (!cancelled && list && storeIdFn) {
            setSavedIds(new Set(list.map((s) => s.store_id || storeIdFn(s))));
          }
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [getSavedStores, storeIdFn]);

  const handlePlaceSelect = useCallback((place) => {
    if (!place) return;
    setSelectedLocation(place);
    setQuery(place.label || place.name);
    setLocationFormOpen(false);
  }, []);

  const searchWithLocation = useCallback(
    async (location, radiusMiles = radius) => {
      setError('');
      setLoading(true);
      try {
        const payload = {
          location: {
            place_id: location.id,
            name: location.label || location.name || 'My location',
            latitude: location.latitude,
            longitude: location.longitude,
          },
          radius_miles: radiusMiles,
          needs: { camp_types: [] },
        };
        const response = await toolkitService.findSupplyStores(payload);
        setResults(Array.isArray(response?.results) ? response.results : []);
        setHighlighted(Array.isArray(response?.highlighted) ? response.highlighted : []);
        setMeta(response?.meta || null);
      } catch {
        setResults([]);
        setHighlighted([]);
        setMeta(null);
        setError('Unable to load stores. Try another location.');
      } finally {
        setLoading(false);
      }
    },
    [radius]
  );

  const handleUseMyLocation = useCallback(() => {
    if (!navigator?.geolocation) {
      setError('Location is not supported by your browser.');
      return;
    }
    setError('');
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          id: 'my-location',
          label: 'My location',
          name: 'My location',
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setSelectedLocation(location);
        setQuery('My location');
        setRadius(MY_LOCATION_RADIUS_MI);
        setLocationFormOpen(false);
        searchWithLocation(location, MY_LOCATION_RADIUS_MI);
      },
      () => {
        setLoading(false);
        setError('Could not get your location. Search for a city or address instead.');
      }
    );
  }, [searchWithLocation]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!selectedLocation) {
        setError('Select a location first, or use "Use my location".');
        return;
      }
      searchWithLocation(selectedLocation, radius);
    },
    [selectedLocation, radius, searchWithLocation]
  );

  const handleSave = useCallback(
    async (store) => {
      if (!saveStore || !storeIdFn) return;
      await saveStore(store);
      setSavedIds((prev) => new Set([...prev, storeIdFn(store)]));
    },
    [saveStore, storeIdFn]
  );

  const handleOpenStore = useCallback((store) => {
    if (store.urls?.website) {
      window.open(store.urls.website, '_blank', 'noopener,noreferrer');
      return;
    }
    if (store.urls?.directions) {
      window.open(store.urls.directions, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const allStores = useMemo(() => {
    const seen = new Set();
    const merged = [];
    [...highlighted, ...results].forEach((store, idx) => {
      const key = store.id || `${store.name}-${idx}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(store);
    });
    return merged;
  }, [highlighted, results]);

  const filteredStores = useMemo(
    () => filterStores(allStores, { searchQuery, categoryId }),
    [allStores, searchQuery, categoryId]
  );

  const locationLabel = selectedLocation?.label || selectedLocation?.name;
  const showLocationBanner = Boolean(locationLabel) && !locationFormOpen;

  return (
    <div className="camp-gear-finder">
      {showLocationBanner ? (
        <div className="camp-essentials-gear__location-banner">
          <div className="camp-essentials-gear__location-banner-main">
            <div className="camp-essentials-gear__location-icon" aria-hidden="true">
              <FaLocationArrow size={18} />
            </div>
            <div>
              <p className="camp-essentials-gear__location-title">
                Showing stores near {locationLabel}
              </p>
              <p className="camp-essentials-gear__location-sub">
                Within {meta?.radius_miles ?? radius} mile radius
                {filteredStores.length > 0 ? ` · ${filteredStores.length} found` : ''}
              </p>
            </div>
          </div>
          <YButton
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setLocationFormOpen(true);
              onChangeLocation?.();
            }}
          >
            <FaMapMarkerAlt aria-hidden="true" />
            Change location
          </YButton>
        </div>
      ) : null}

      <div
        ref={ref}
        className={`camp-gear-finder__search-panel${locationFormOpen || !showLocationBanner ? '' : ' camp-gear-finder__search-panel--hidden'}`}
      >
        <form className="camp-gear-finder__form" onSubmit={handleSubmit}>
          <label className="form-label" htmlFor="camp-gear-location-input">
            Where do you need gear?
          </label>
          <div className="y-finder__field d-flex flex-wrap gap-2 align-items-center">
            <div className="flex-grow-1 min-w-200">
              <PlaceSearchInput
                inputId="camp-gear-location-input"
                value={query}
                onChange={setQuery}
                onSelect={handlePlaceSelect}
                placeholder="Search city, town, or address"
                limit={6}
                aria-label="Search location for nearby gear stores"
              />
            </div>
            <YButton type="button" variant="outline-secondary" onClick={handleUseMyLocation} disabled={loading}>
              Use my location
            </YButton>
          </div>

          <div className="y-finder__controls mt-3">
            <label className="form-label" htmlFor="camp-gear-radius">
              Search radius
            </label>
            <YSelect
              id="camp-gear-radius"
              value={String(radius)}
              onChange={(e) => setRadius(Number(e.target.value))}
            >
              {RADIUS_CHOICES.map((r) => (
                <option key={r} value={r}>
                  {r} miles
                </option>
              ))}
            </YSelect>
          </div>

          <div className="d-flex align-items-center gap-3 mt-3">
            <YButton type="submit" disabled={loading}>
              {loading ? 'Searching…' : 'Find gear stores'}
            </YButton>
          </div>
          {error ? <div className="text-danger small mt-2">{error}</div> : null}
        </form>
      </div>

      {!locationLabel && !loading && (
        <p className="camp-gear-finder__empty">
          Search for a city or use your location to find RV, trailer, ATV, and outdoor retailers
          near camp.
        </p>
      )}

      {loading && filteredStores.length === 0 ? (
        <p className="camp-gear-finder__empty">Searching for stores…</p>
      ) : null}

      {!loading && locationLabel && filteredStores.length === 0 ? (
        <p className="camp-gear-finder__empty">
          No stores match your filters. Try another category or widen your search radius.
        </p>
      ) : null}

      {filteredStores.length > 0 ? (
        <div
          className={`camp-gear-stores__grid camp-gear-stores__grid--${viewMode}`}
          data-testid="camp-gear-store-results"
        >
          {filteredStores.map((store, index) => (
            <StoreCard
              key={store.id || `${store.name}-${index}`}
              store={store}
              viewMode={viewMode}
              savedIds={savedIds}
              onSave={handleSave}
              storeId={storeIdFn}
              onOpen={handleOpenStore}
            />
          ))}
        </div>
      ) : null}

      {meta && allStores.length > 0 ? (
        <div className="y-finder__meta text-muted small mt-3">
          {meta.curated_count} partners · {meta.mapbox_count} map matches
          {meta.attribution ? <div className="mt-1">{meta.attribution}</div> : null}
        </div>
      ) : null}

      {onSuggestStore ? (
        <div className="y-finder__suggest mt-3">
          <YButton type="button" variant="ghost" size="sm" onClick={onSuggestStore}>
            Suggest a store
          </YButton>
        </div>
      ) : null}
    </div>
  );
});
